import { useEffect, useState } from 'react';
import { XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { ImageUrl } from '../services/API';

export default function BulletinModal({ open, onClose, bulletin = null, onSave }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'medium',
    coverImage: null,
    attachments: []
  });

  const [errors, setErrors] = useState({});
  const [fileNames, setFileNames] = useState([]);
  const [coverImageName, setCoverImageName] = useState('');
  const [coverPreviewUrl, setCoverPreviewUrl] = useState('');
  const [existingAttachments, setExistingAttachments] = useState([]);

  useEffect(() => {
    if (bulletin) {
      setFormData({
        title: bulletin.title || '',
        description: bulletin.message || '',
        category: bulletin.category || '',
        priority: bulletin.priority || 'medium',
        coverImage: null,
        attachments: [] // Start fresh with empty attachments for new uploads
      });
      setFileNames([]); // Clear file names for new uploads
      setCoverImageName('');
      setCoverPreviewUrl( `${ImageUrl}/${bulletin.coverImage}`|| '');
      setExistingAttachments(Array.isArray(bulletin.attachments) ? bulletin.attachments : []);
    } else {
      setFormData({
        title: '',
        description: '',
        category: '',
        priority: 'medium',
        coverImage: null,
        attachments: []
      });
      setFileNames([]);
      setCoverImageName('');
      setCoverPreviewUrl('');
      setExistingAttachments([]);
    }
  }, [bulletin, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handlePriorityChange = (value) => {
    setFormData(prev => ({
      ...prev,
      priority: value
    }));
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    setFileNames(prev => prev.filter((_, i) => i !== index));
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCoverImageName(file.name);
      setFormData(prev => ({
        ...prev,
        coverImage: file
      }));
      try {
        const preview = URL.createObjectURL(file);
        setCoverPreviewUrl(preview);
      } catch (_) {}
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const newFileNames = files.map(file => file.name);
    
    setFileNames(prev => [...prev, ...newFileNames]);
    
    // Store actual File objects for API upload
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validate()) {
      try {
        // Include the bulletin ID if we're editing an existing bulletin
        const dataToSave = bulletin ? { ...formData, id: bulletin._id,attachments:null } : formData;
        await onSave(dataToSave);
        onClose();
      } catch (error) {
        console.error('Error saving bulletin:', error);
        // Don't close modal on error, let the parent handle the error message
      }
    }
  };

  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-10 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={onClose} />
      
      <div className="flex min-h-full items-center justify-center p-4 text-center">
        <div className="relative transform overflow-hidden rounded-lg bg-[#212C43] text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
          <div className="bg-[#212C43] px-6 pt-6 pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                <h3 className="text-lg font-semibold leading-6 text-white flex justify-between items-center mb-6">
                  {bulletin ? 'Edit Bulletin' : 'Add New Bulletin'}
                  <button
                    type="button"
                    className="rounded-md bg-[#212C43] text-gray-400 hover:text-white"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </h3>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Title and Category in same row */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleChange}
                        className={`w-full px-3 py-3 border-2 rounded-md bg-[#384355] text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.title ? 'border-red-500' : 'border-borderGray'}`}
                        placeholder="Enter bulletin title"
                      />
                      {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-white mb-2">
                        Category *
                      </label>
                      <select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        className={`w-full px-3 py-3 border-2 rounded-md bg-[#384355] text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.category ? 'border-red-500' : 'border-borderGray'}`}
                      >
                        <option value="">Select Category</option>
                        <option value="alerts">Alerts</option>
                        <option value="updates">Updates</option>
                        <option value="news">News</option>
                        <option value="maintenance">Maintenance</option>
                        <option value="infrastructure">Infrastructure</option>
                        <option value="emergency">Emergency</option>
                      </select>
                      {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
                </div>
              </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">
                      Description *
                    </label>
                    <textarea
                      name="description"
                      rows={7}
                      value={formData.description}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border-2 rounded-md bg-[#384355] text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.description ? 'border-red-500' : 'border-borderGray'}`}
                      placeholder="Enter bulletin description"
                    />
                    {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                  </div>

                  {/* Priority - Radio Buttons */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-3">
                      Priority
                    </label>
                    <div className="flex space-x-6">
                      {[
                        { value: 'low', label: 'Low', color: 'text-green-400' },
                        { value: 'medium', label: 'Medium', color: 'text-yellow-400' },
                        { value: 'high', label: 'High', color: 'text-orange-400' },
                        { value: 'urgent', label: 'Urgent', color: 'text-red-400' }
                      ].map((priority) => (
                        <label key={priority.value} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="priority"
                            value={priority.value}
                            checked={formData.priority === priority.value}
                            onChange={(e) => handlePriorityChange(e.target.value)}
                            className="mr-2 text-blue-600 focus:ring-blue-500"
                          />
                          <span className={`text-sm ${priority.color}`}>{priority.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Cover Image (moved below Priority) */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-2">Cover Image</label>
                 <div
  className="mt-2 bg-[#384355] flex justify-center rounded-lg border-2 border-dashed border-gray-400 px-6 py-10 cursor-pointer"
  onClick={() => document.getElementById('cover-image-upload').click()}
>
  <div className="text-center">
    {coverPreviewUrl ? (
      <img
        src={coverPreviewUrl}
        alt="Cover preview"
        className="mx-auto h-40 w-auto rounded-md object-cover"
      />
    ) : (
      <CloudArrowUpIcon
        className="mx-auto h-12 w-12 text-gray-300"
        aria-hidden="true"
      />
    )}
    <div className="mt-4 flex text-sm leading-6 text-gray-400 justify-center">
      <label
        htmlFor="cover-image-upload"
        className="relative cursor-pointer rounded-md  font-semibold text-white focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-600 focus-within:ring-offset-2 focus-within:ring-offset-gray-800 hover:text-blue-400"
      >
        <input
          id="cover-image-upload"
          name="cover-image-upload"
          type="file"
          className="sr-only"
          onChange={handleCoverImageChange}
          accept="image/*"
        />
      </label>
      <p className="pl-1">Click to this area for uploading</p>
    </div>
    <p className="text-xs leading-5 text-gray-400">PNG, JPG, GIF up to 10MB</p>
    {coverImageName && <p className="text-sm text-gray-300 mt-2">{coverImageName}</p>}
  </div>
</div>

                  </div>

                  {/* Attachments - New Design */}
                  <div>
                    <label className="block text-sm font-medium text-white mb-3">
                      Attachments
                    </label>
                    {bulletin ? (
                      <div className="bg-[#384355] border-2 border-borderGray rounded-md p-4">
                        {existingAttachments && existingAttachments.length > 0 ? (
                          <ul className="space-y-2">
                            {existingAttachments.map((att, index) => {
                              const name = att?.name || att?.filename || att?.originalName || (att?.url ? att.url.split('/').pop() : `Attachment ${index + 1}`);
                              return (
                                <li key={index} className="flex items-center justify-between text-sm text-gray-300">
                                  <span className="truncate flex-1">{name}</span>
                                  {att?.url && (
                                    <a href={att.url} target="_blank" rel="noreferrer" className="ml-3 text-blue-400 hover:text-blue-300">View</a>
                                  )}
                                </li>
                              );
                            })}
                          </ul>
                        ) : (
                          <p className="text-gray-300 text-sm">No attachments</p>
                        )}
                        <p className="mt-3 text-xs text-gray-400">Attachments are read-only in edit mode.</p>
                      </div>
                    ) : (
                      <>
                        <div 
                          className={`w-full border-2 border-dashed border-gray-500 rounded-lg p-8 text-center bg-[#384355] hover:bg-[#384355]/80 transition-colors cursor-pointer ${
                            formData.attachments.length >= 5 ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          onClick={() => {
                            if (formData.attachments.length < 5) {
                              document.getElementById('attachments').click();
                            }
                          }}
                        >
                          <input
                            type="file"
                            id="attachments"
                            multiple
                            accept=".jpg,.jpeg,.png,.pdf,.xls,.xlsx,.doc,.docx"
                            onChange={handleFileChange}
                            className="hidden"
                            disabled={formData.attachments.length >= 5}
                          />
                          <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-300 text-sm">
                            {formData.attachments.length >= 5 ? 'Maximum 5 files reached' : 'Click to this area for uploading'}
                          </p>
                          <p className="text-gray-500 text-xs mt-1">
                            Support: Images, PDF, Excel, Word (Max 5 files)
                          </p>
                        </div>
                        {fileNames.length > 0 && (
                          <div className="mt-3">
                            <p className="text-sm text-gray-300 mb-2">Selected files:</p>
                            <ul className="space-y-2">
                              {fileNames.map((name, index) => (
                                <li key={index} className="flex items-center justify-between text-sm text-gray-400 bg-[#384355] px-3 py-2 rounded">
                                  <span className="truncate flex-1">{name}</span>
                                  <button
                                    type="button"
                                    onClick={() => removeAttachment(index)}
                                    className="ml-2 text-red-400 hover:text-red-300"
                                  >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      className="px-4 py-2 text-sm font-medium text-gray-300 bg-[#384355] rounded-md hover:bg-[#384355]/80 focus:outline-none focus:ring-2 focus:ring-gray-500"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      {bulletin ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}